import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { AlertTriangle } from 'lucide-react'

export type UnsavedAction = 'cancel' | 'leave' | 'discard'

interface UnsavedChangesDialogProps {
  fileNames: string[]
  onAction: (action: UnsavedAction) => void
}

export function UnsavedChangesDialog({ fileNames, onAction }: UnsavedChangesDialogProps) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const fileList = fileNames.length === 1
    ? `"${fileNames[0]}"`
    : `${fileNames.length} files (${fileNames.join(', ')})`

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 animate-fade-in"
        onClick={() => onAction('cancel')}
      />

      {/* Modal */}
      <div className={`relative z-10 rounded-lg shadow-xl max-w-md w-full animate-scale-in ${themeClass} themed-bg-secondary`}>
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="shrink-0 mt-0.5">
              <AlertTriangle size={24} className="text-amber-500" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold themed-text mb-2">
                Unsaved Changes
              </h3>
              <p className="themed-text-secondary text-sm">
                {fileList} has unsaved changes. What would you like to do?
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t themed-border">
          <button
            onClick={() => onAction('cancel')}
            className="px-4 py-2 themed-btn-secondary rounded-lg text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          {/*
            "Keep draft", not "Leave Without Saving". This branch does NOT
            discard anything: the tab closes, the room stays dirty, and the
            edits are persisted to `yjs_documents` when the room is evicted —
            reopening the file brings them back, still unsaved. The old label
            described a loss that does not happen, next to a button that really
            does destroy the work, so the two read as variations of the same
            outcome. Naming the state papan actually keeps — a draft — is what
            distinguishes them. See docs/SYNC_AND_CONFLICT_RESOLUTION.md
            ("Version History") for why saving is not offered here: only an
            intentional save creates a version, and closing a tab is not one.
          */}
          <button
            onClick={() => onAction('leave')}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Keep draft
          </button>
          <button
            onClick={() => onAction('discard')}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Discard
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
